'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function VatCalculator() {
  const t = useTranslations("tools.vatCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    vatRate: '',
    netPrice: '',
    grossPrice: '',
    taxAmount: ''
  });
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const baseStyles = {
    container: {
      maxWidth: '1200px',
      margin: '0 auto',
      position: 'relative' as const,
      zIndex: 1,
    },
    header: {
      textAlign: 'center' as const,
      marginBottom: '2rem',
    },
    h1: {
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '1rem',
      lineHeight: 1.2,
    },
    summaryText: {
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    calculatorTool: {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
    },
    toolContainer: {
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    toolHeader: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '2rem',
      textAlign: 'center' as const,
    },
    toolHeaderTitle: {
      margin: 0,
      fontWeight: 700,
    },
    calculatorForm: {
      padding: '2rem',
    },
    calculationMode: {
      background: '#f8fafc',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem',
      borderLeft: '4px solid #7c3aed',
    },
    calculationModeText: {
      margin: 0,
      color: '#475569',
      fontSize: '0.9rem',
    },
    formGrid: {
      display: 'grid',
      gap: '1.5rem',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '0.5rem',
    },
    label: {
      fontWeight: 600,
      color: colors.textPrimary,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: '0.95rem',
    },
    helpIcon: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '18px',
      height: '18px',
      background: '#e0f2fe',
      color: '#0284c7',
      borderRadius: '50%',
      fontSize: '0.75rem',
      fontWeight: 700,
      cursor: 'help',
      position: 'relative' as const,
    },
    inputWrapper: {
      position: 'relative' as const,
      display: 'flex',
      alignItems: 'center',
    },
    currencySymbol: {
      position: 'absolute' as const,
      left: '1rem',
      color: '#64748b',
      fontWeight: 600,
      pointerEvents: 'none' as const,
    },
    percentSymbol: {
      position: 'absolute' as const,
      right: '1rem',
      color: '#64748b',
      fontWeight: 600,
      pointerEvents: 'none' as const,
    },
    input: {
      width: '100%',
      padding: '0.875rem 1rem',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
    },
    inputWithCurrency: {
      paddingLeft: '2.5rem',
    },
    inputWithPercent: {
      paddingRight: '2.5rem',
    },
    buttonGroup: {
      display: 'flex',
      gap: '1rem',
      marginTop: '2rem',
    },
    calculateButton: {
      flex: 1,
      padding: '1rem',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
    },
    clearButton: {
      flex: 1,
      padding: '1rem',
      border: 'none',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
    },
    errorMessage: {
      background: '#fee2e2',
      color: '#991b1b',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginTop: '1rem',
      borderLeft: '4px solid #dc2626',
    },
    results: {
      padding: '2rem',
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderTop: '3px solid #f59e0b',
    },
    resultsTitle: {
      margin: '0 0 1.5rem 0',
      color: '#92400e',
      fontWeight: 600,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    resultsGrid: {
      display: 'grid',
      gap: '1rem',
    },
    resultItem: {
      background: 'white',
      padding: '1.25rem',
      borderRadius: '0.75rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    },
    resultItemHighlight: {
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      border: '2px solid #2563eb',
    },
    resultLabel: {
      fontWeight: 600,
      color: colors.textPrimary,
      fontSize: '0.95rem',
    },
    resultValue: {
      fontWeight: 700,
      color: '#2563eb',
    },
    glassCard: {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    h2: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
    },
    paragraph: {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem',
    },
    list: {
      paddingLeft: '1.5rem',
      marginBottom: '1rem',
    },
    listItem: {
      marginBottom: '0.75rem',
      lineHeight: 1.6,
      color: colors.textSecondary,
    },
    insightCard: {
      background: 'rgba(255, 237, 213, 0.3)',
      padding: '1rem',
      borderRadius: '0.5rem',
      marginBottom: '1rem',
      borderLeft: '4px solid #f59e0b',
    },
    insightTitle: {
      fontWeight: 600,
      color: '#92400e',
      marginBottom: '0.5rem',
      fontSize: '0.95rem',
    },
    insightText: {
      fontSize: '0.9rem',
      color: colors.textSecondary,
      margin: 0,
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '0.5rem',
      fontSize: '1.05rem',
    },
    faqAnswer: {
      color: colors.textSecondary,
      lineHeight: 1.6,
      margin: 0,
    },
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      display: 'block',
      marginBottom: '0.5rem',
      transition: 'color 0.3s ease',
    },
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const vatRate = inputs.vatRate !== '' ? parseFloat(inputs.vatRate) : null;
    const netPrice = inputs.netPrice !== '' ? parseFloat(inputs.netPrice) : null;
    const grossPrice = inputs.grossPrice !== '' ? parseFloat(inputs.grossPrice) : null;
    const taxAmount = inputs.taxAmount !== '' ? parseFloat(inputs.taxAmount) : null;

    const filledCount = [vatRate, netPrice, grossPrice, taxAmount].filter(v => v !== null).length;

    if (filledCount < 2) {
      setError(t('errorMinimumInputs'));
      setResults(null);
      return;
    }

    let calculatedVatRate = vatRate;
    let calculatedNetPrice = netPrice;
    let calculatedGrossPrice = grossPrice;
    let calculatedTaxAmount = taxAmount;

    if (vatRate !== null && netPrice !== null) {
      calculatedTaxAmount = netPrice * (vatRate / 100);
      calculatedGrossPrice = netPrice + calculatedTaxAmount;
    } else if (vatRate !== null && grossPrice !== null) {
      calculatedNetPrice = grossPrice / (1 + vatRate / 100);
      calculatedTaxAmount = grossPrice - calculatedNetPrice;
    } else if (vatRate !== null && taxAmount !== null) {
      calculatedNetPrice = taxAmount / (vatRate / 100);
      calculatedGrossPrice = calculatedNetPrice + taxAmount;
    } else if (netPrice !== null && grossPrice !== null) {
      calculatedTaxAmount = grossPrice - netPrice;
      calculatedVatRate = (calculatedTaxAmount / netPrice) * 100;
    } else if (netPrice !== null && taxAmount !== null) {
      calculatedVatRate = (taxAmount / netPrice) * 100;
      calculatedGrossPrice = netPrice + taxAmount;
    } else if (grossPrice !== null && taxAmount !== null) {
      calculatedNetPrice = grossPrice - taxAmount;
      calculatedVatRate = (taxAmount / calculatedNetPrice) * 100;
    } else {
      setError(t('errorInvalidCombination'));
      setResults(null);
      return;
    }

    if (calculatedVatRate! < 0 || calculatedNetPrice! < 0 || calculatedGrossPrice! < 0 || calculatedTaxAmount! < 0) {
      setError(t('errorNegativeValues'));
      setResults(null);
      return;
    }

    setInputs({
      vatRate: calculatedVatRate!.toFixed(2),
      netPrice: calculatedNetPrice!.toFixed(2),
      grossPrice: calculatedGrossPrice!.toFixed(2),
      taxAmount: calculatedTaxAmount!.toFixed(2),
    });

    setResults({
      vatRate: calculatedVatRate,
      netPrice: calculatedNetPrice,
      grossPrice: calculatedGrossPrice,
      taxAmount: calculatedTaxAmount,
    });
  };

  const handleClear = () => {
    setInputs({
      vatRate: '',
      netPrice: '',
      grossPrice: '',
      taxAmount: ''
    });
    setResults(null);
    setError('');
  };

  const formatCurrency = (value: number) => {
    return '$' + value.toFixed(2);
  };

  const formatPercent = (value: number) => {
    return value.toFixed(2) + '%';
  };

  return (
    <div style={getResponsiveStyle(
      baseStyles.container,
      { padding: '1rem' },
      { padding: '2rem' },
      { padding: '3rem' }
    )}>
      {/* Header */}
      <header style={getResponsiveStyle(
        baseStyles.header,
        { marginBottom: '2rem' },
        { marginBottom: '2.5rem' },
        { marginBottom: '3rem' }
      )}>
        <h1 style={getResponsiveStyle(
          baseStyles.h1,
          { fontSize: '2rem' },
          { fontSize: '2.5rem' },
          { fontSize: '3rem' }
        )}>{t('title')}</h1>
        <p style={getResponsiveStyle(
          baseStyles.summaryText,
          { fontSize: '1rem' },
          { fontSize: '1.125rem' },
          { fontSize: '1.25rem' }
        )}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={getResponsiveStyle(
        baseStyles.calculatorTool,
        { padding: '1.5rem 1rem' },
        { padding: '2rem 1.5rem' },
        { padding: '3rem 2rem' }
      )}>
        <div style={getResponsiveStyle(
          baseStyles.toolContainer,
          { maxWidth: '100%' },
          { maxWidth: '600px' },
          { maxWidth: '800px' }
        )}>
          <div style={baseStyles.toolHeader}>
            <h2 style={getResponsiveStyle(
              baseStyles.toolHeaderTitle,
              { fontSize: '1.5rem' },
              { fontSize: '1.75rem' },
              { fontSize: '2rem' }
            )}>{t('toolTitle')}</h2>
          </div>

          <form style={baseStyles.calculatorForm} onSubmit={handleCalculate}>
            <div style={baseStyles.calculationMode}>
              <p style={baseStyles.calculationModeText}>
                <strong>{t('modeLabel')}</strong> {t('modeDescription')}
              </p>
            </div>

            <div style={baseStyles.formGrid}>
              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label} htmlFor="vat-rate">
                  {t('vatRateLabel')}
                  <span style={baseStyles.helpIcon} title={t('vatRateTooltip')}>?</span>
                </label>
                <div style={baseStyles.inputWrapper}>
                  <input
                    type="number"
                    id="vat-rate"
                    step="0.01"
                    min="0"
                    max="100"
                    value={inputs.vatRate}
                    onChange={(e) => setInputs({...inputs, vatRate: e.target.value})}
                    style={{...baseStyles.input, ...baseStyles.inputWithPercent}}
                  />
                  <span style={baseStyles.percentSymbol}>%</span>
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label} htmlFor="net-price">
                  {t('netPriceLabel')}
                  <span style={baseStyles.helpIcon} title={t('netPriceTooltip')}>?</span>
                </label>
                <div style={baseStyles.inputWrapper}>
                  <span style={baseStyles.currencySymbol}>$</span>
                  <input
                    type="number"
                    id="net-price"
                    step="0.01"
                    min="0"
                    value={inputs.netPrice}
                    onChange={(e) => setInputs({...inputs, netPrice: e.target.value})}
                    style={{...baseStyles.input, ...baseStyles.inputWithCurrency}}
                  />
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label} htmlFor="gross-price">
                  {t('grossPriceLabel')}
                  <span style={baseStyles.helpIcon} title={t('grossPriceTooltip')}>?</span>
                </label>
                <div style={baseStyles.inputWrapper}>
                  <span style={baseStyles.currencySymbol}>$</span>
                  <input
                    type="number"
                    id="gross-price"
                    step="0.01"
                    min="0"
                    value={inputs.grossPrice}
                    onChange={(e) => setInputs({...inputs, grossPrice: e.target.value})}
                    style={{...baseStyles.input, ...baseStyles.inputWithCurrency}}
                  />
                </div>
              </div>

              <div style={baseStyles.formGroup}>
                <label style={baseStyles.label} htmlFor="tax-amount">
                  {t('taxAmountLabel')}
                  <span style={baseStyles.helpIcon} title={t('taxAmountTooltip')}>?</span>
                </label>
                <div style={baseStyles.inputWrapper}>
                  <span style={baseStyles.currencySymbol}>$</span>
                  <input
                    type="number"
                    id="tax-amount"
                    step="0.01"
                    min="0"
                    value={inputs.taxAmount}
                    onChange={(e) => setInputs({...inputs, taxAmount: e.target.value})}
                    style={{...baseStyles.input, ...baseStyles.inputWithCurrency}}
                  />
                </div>
              </div>
            </div>

            {error && (
              <div style={baseStyles.errorMessage}>
                {error}
              </div>
            )}

            <div style={getResponsiveStyle(
              baseStyles.buttonGroup,
              { flexDirection: 'column' },
              { flexDirection: 'row' },
              { flexDirection: 'row' }
            )}>
              <button type="submit" style={baseStyles.calculateButton}>
                {t('calculateButton')}
              </button>
              <button type="button" style={baseStyles.clearButton} onClick={handleClear}>
                {t('clearButton')}
              </button>
            </div>
          </form>

          {results && (
            <div style={baseStyles.results}>
              <h3 style={getResponsiveStyle(
                baseStyles.resultsTitle,
                { fontSize: '1.25rem' },
                { fontSize: '1.375rem' },
                { fontSize: '1.5rem' }
              )}>{t('resultsTitle')}</h3>
              <div style={baseStyles.resultsGrid}>
                <div style={baseStyles.resultItem}>
                  <span style={baseStyles.resultLabel}>{t('vatRateLabel')}:</span>
                  <span style={getResponsiveStyle(
                    baseStyles.resultValue,
                    { fontSize: '1.25rem' },
                    { fontSize: '1.375rem' },
                    { fontSize: '1.5rem' }
                  )}>{formatPercent(results.vatRate)}</span>
                </div>
                <div style={baseStyles.resultItem}>
                  <span style={baseStyles.resultLabel}>{t('netPriceLabel')}:</span>
                  <span style={getResponsiveStyle(
                    baseStyles.resultValue,
                    { fontSize: '1.25rem' },
                    { fontSize: '1.375rem' },
                    { fontSize: '1.5rem' }
                  )}>{formatCurrency(results.netPrice)}</span>
                </div>
                <div style={{...baseStyles.resultItem, ...baseStyles.resultItemHighlight}}>
                  <span style={baseStyles.resultLabel}>{t('grossPriceLabel')}:</span>
                  <span style={getResponsiveStyle(
                    baseStyles.resultValue,
                    { fontSize: '1.25rem' },
                    { fontSize: '1.375rem' },
                    { fontSize: '1.5rem' }
                  )}>{formatCurrency(results.grossPrice)}</span>
                </div>
                <div style={baseStyles.resultItem}>
                  <span style={baseStyles.resultLabel}>{t('taxAmountLabel')}:</span>
                  <span style={getResponsiveStyle(
                    baseStyles.resultValue,
                    { fontSize: '1.25rem' },
                    { fontSize: '1.375rem' },
                    { fontSize: '1.5rem' }
                  )}>{formatCurrency(results.taxAmount)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={getResponsiveStyle(
        baseStyles.glassCard,
        { padding: '1.5rem' },
        { padding: '2rem' },
        { padding: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(
          baseStyles.h2,
          { fontSize: '1.5rem' },
          { fontSize: '1.75rem' },
          { fontSize: '2rem' }
        )}>{t('introTitle')}</h2>
        <p style={getResponsiveStyle(
          baseStyles.paragraph,
          { fontSize: '1rem' },
          { fontSize: '1.125rem' },
          { fontSize: '1.25rem' }
        )}>{t('introParagraph1')}</p>
        <p style={getResponsiveStyle(
          baseStyles.paragraph,
          { fontSize: '1rem' },
          { fontSize: '1.125rem' },
          { fontSize: '1.25rem' }
        )}>{t('introParagraph2')}</p>
        <p style={getResponsiveStyle(
          baseStyles.paragraph,
          { fontSize: '1rem' },
          { fontSize: '1.125rem' },
          { fontSize: '1.25rem' }
        )}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={getResponsiveStyle(
        baseStyles.glassCard,
        { padding: '1.5rem' },
        { padding: '2rem' },
        { padding: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(
          baseStyles.h2,
          { fontSize: '1.5rem' },
          { fontSize: '1.75rem' },
          { fontSize: '2rem' }
        )}>{t('howToUseTitle')}</h2>
        <ol style={baseStyles.list}>
          <li style={getResponsiveStyle(
            baseStyles.listItem,
            { fontSize: '1rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('howToUseStep1')}</li>
          <li style={getResponsiveStyle(
            baseStyles.listItem,
            { fontSize: '1rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('howToUseStep2')}</li>
          <li style={getResponsiveStyle(
            baseStyles.listItem,
            { fontSize: '1rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('howToUseStep3')}</li>
          <li style={getResponsiveStyle(
            baseStyles.listItem,
            { fontSize: '1rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('howToUseStep4')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={getResponsiveStyle(
        baseStyles.glassCard,
        { padding: '1.5rem' },
        { padding: '2rem' },
        { padding: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(
          baseStyles.h2,
          { fontSize: '1.5rem' },
          { fontSize: '1.75rem' },
          { fontSize: '2rem' }
        )}>{t('insightsTitle')}</h2>

        <div style={baseStyles.insightCard}>
          <h3 style={baseStyles.insightTitle}>{t('insight1Title')}</h3>
          <p style={baseStyles.insightText}>{t('insight1Text')}</p>
        </div>

        <div style={baseStyles.insightCard}>
          <h3 style={baseStyles.insightTitle}>{t('insight2Title')}</h3>
          <p style={baseStyles.insightText}>{t('insight2Text')}</p>
        </div>

        <div style={baseStyles.insightCard}>
          <h3 style={baseStyles.insightTitle}>{t('insight3Title')}</h3>
          <p style={baseStyles.insightText}>{t('insight3Text')}</p>
        </div>
      </section>

      {/* Details */}
      <section style={getResponsiveStyle(
        baseStyles.glassCard,
        { padding: '1.5rem' },
        { padding: '2rem' },
        { padding: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(
          baseStyles.h2,
          { fontSize: '1.5rem' },
          { fontSize: '1.75rem' },
          { fontSize: '2rem' }
        )}>{t('detailsTitle')}</h2>
        <p style={getResponsiveStyle(
          baseStyles.paragraph,
          { fontSize: '1rem' },
          { fontSize: '1.125rem' },
          { fontSize: '1.25rem' }
        )}>{t('detailsParagraph1')}</p>
        <p style={getResponsiveStyle(
          baseStyles.paragraph,
          { fontSize: '1rem' },
          { fontSize: '1.125rem' },
          { fontSize: '1.25rem' }
        )}>{t('detailsParagraph2')}</p>
        <p style={getResponsiveStyle(
          baseStyles.paragraph,
          { fontSize: '1rem' },
          { fontSize: '1.125rem' },
          { fontSize: '1.25rem' }
        )}>{t('detailsParagraph3')}</p>
      </section>

      {/* FAQ */}
      <section style={getResponsiveStyle(
        baseStyles.glassCard,
        { padding: '1.5rem' },
        { padding: '2rem' },
        { padding: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(
          baseStyles.h2,
          { fontSize: '1.5rem' },
          { fontSize: '1.75rem' },
          { fontSize: '2rem' }
        )}>{t('faqTitle')}</h2>

        <div style={baseStyles.faqItem}>
          <h3 style={getResponsiveStyle(
            baseStyles.faqQuestion,
            { fontSize: '1.05rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('faq1Question')}</h3>
          <p style={getResponsiveStyle(
            baseStyles.faqAnswer,
            { fontSize: '1rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('faq1Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={getResponsiveStyle(
            baseStyles.faqQuestion,
            { fontSize: '1.05rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('faq2Question')}</h3>
          <p style={getResponsiveStyle(
            baseStyles.faqAnswer,
            { fontSize: '1rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('faq2Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={getResponsiveStyle(
            baseStyles.faqQuestion,
            { fontSize: '1.05rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('faq3Question')}</h3>
          <p style={getResponsiveStyle(
            baseStyles.faqAnswer,
            { fontSize: '1rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('faq3Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={getResponsiveStyle(
            baseStyles.faqQuestion,
            { fontSize: '1.05rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('faq4Question')}</h3>
          <p style={getResponsiveStyle(
            baseStyles.faqAnswer,
            { fontSize: '1rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('faq4Answer')}</p>
        </div>

        <div style={baseStyles.faqItem}>
          <h3 style={getResponsiveStyle(
            baseStyles.faqQuestion,
            { fontSize: '1.05rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('faq5Question')}</h3>
          <p style={getResponsiveStyle(
            baseStyles.faqAnswer,
            { fontSize: '1rem' },
            { fontSize: '1.125rem' },
            { fontSize: '1.25rem' }
          )}>{t('faq5Answer')}</p>
        </div>
      </section>

      {/* References */}
      <section style={getResponsiveStyle(
        baseStyles.glassCard,
        { padding: '1.5rem' },
        { padding: '2rem' },
        { padding: '2.5rem' }
      )}>
        <h2 style={getResponsiveStyle(
          baseStyles.h2,
          { fontSize: '1.5rem' },
          { fontSize: '1.75rem' },
          { fontSize: '2rem' }
        )}>{t('referencesTitle')}</h2>
        <a
          href="https://kt.wacontre.com/en/ke-toan-thue-en/8-value-added-tax-vat-reduction-in-2025-new-policy-and-application-guidelines/"
          target="_blank"
          rel="noopener noreferrer"
          style={baseStyles.referenceLink}
        >
          {t('reference1')}
        </a>
        <a
          href="https://www.bisse.fi/en/school/verotus/alv_opas"
          target="_blank"
          rel="noopener noreferrer"
          style={baseStyles.referenceLink}
        >
          {t('reference2')}
        </a>
        <a
          href="https://ca.indeed.com/career-advice/career-development/vat-calculation"
          target="_blank"
          rel="noopener noreferrer"
          style={baseStyles.referenceLink}
        >
          {t('reference3')}
        </a>
        <a
          href="https://www.cpapracticeadvisor.com/2025/02/07/a-guide-to-understanding-value-added-tax-vat/155710/"
          target="_blank"
          rel="noopener noreferrer"
          style={baseStyles.referenceLink}
        >
          {t('reference4')}
        </a>
        <a
          href="https://corporatefinanceinstitute.com/resources/accounting/value-added-tax-vat/"
          target="_blank"
          rel="noopener noreferrer"
          style={baseStyles.referenceLink}
        >
          {t('reference5')}
        </a>
      </section>
    </div>
  );
}